import { AlertTriangle, RefreshCw } from "lucide-react";
import { useEffect, useMemo } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { Button } from "@/components/ui/button";

const chunkRecoveryKey = "master-notes:chunk-recovery-at";
const chunkRecoveryCooldownMs = 10_000;
const cacheBusterParameter = "_mn_reload";

function errorText(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }

  if (isRouteErrorResponse(error)) {
    const detail =
      error.data instanceof Error
        ? `${error.data.name}: ${error.data.message}`
        : String(error.data ?? error.statusText);
    return `${error.status} ${error.statusText}: ${detail}`;
  }

  return String(error);
}

export function isStaleChunkError(error: unknown): boolean {
  const message = errorText(error).toLocaleLowerCase();
  return [
    "not a valid javascript mime type",
    "failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "importing a module script failed",
    "chunkloaderror",
    "loading chunk",
    "unable to preload css",
  ].some((fragment) => message.includes(fragment));
}

function reloadWithFreshAssets() {
  const url = new URL(window.location.href);
  url.searchParams.set(cacheBusterParameter, Date.now().toString());
  window.location.replace(url);
}

export function RouteErrorPage() {
  const error = useRouteError();
  const staleChunk = useMemo(() => isStaleChunkError(error), [error]);

  useEffect(() => {
    if (!staleChunk) return;

    const lastAttempt = Number(sessionStorage.getItem(chunkRecoveryKey) ?? "0");
    if (Date.now() - lastAttempt < chunkRecoveryCooldownMs) return;

    sessionStorage.setItem(chunkRecoveryKey, String(Date.now()));
    reloadWithFreshAssets();
  }, [staleChunk]);

  return (
    <main className="grid min-h-screen place-items-center bg-slate-50 p-6">
      <div className="max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl shadow-slate-200/50">
        <AlertTriangle className="mx-auto mb-4 size-10 text-amber-500" />
        <h1 className="text-2xl font-bold text-slate-950">
          {staleChunk ? "A newer version is available" : "This page hit a problem"}
        </h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          {staleChunk
            ? "Master Notes is refreshing to load the latest application files."
            : "Your work is still safe. Refresh the page to try again."}
        </p>
        <Button className="mt-6" onClick={reloadWithFreshAssets}>
          <RefreshCw className="size-4" />
          Refresh application
        </Button>
      </div>
    </main>
  );
}
